export type ResolutionOption = {
  value: string;
  disable?: boolean;
  [key: string]: any;
};

export type SatelliteVuModeLike = {
  resolution?: string[];
  [key: string]: any;
};

export type SensorRowWithResolution = {
  resolution?: ResolutionOption[];
  selectedResolution?: ResolutionOption | null;
  selectedResolutionValue?: string | null;
  [key: string]: any;
};

function normalizeBandsText(bands: any): string | null {
  if (bands == null) return null;
  if (Array.isArray(bands)) return bands.filter(Boolean).join('\n');
  if (typeof bands === 'string') return bands;
  return String(bands);
}

/**
 * Applies SatelliteVu_VanZyl mode-based resolution constraints.
 *
 * - Accepts `productMode` as either a single mode object or an array containing the selected mode.
 * - Disables any resolutions not present in `mode.resolution`.
 * - Picks the first allowed resolution as preferred, otherwise first enabled.
 *
 * Returns true when constraints were applied (i.e., a `mode.resolution` list existed).
 */
export function applySatelliteVuResolutionConstraints(
  row: SensorRowWithResolution,
  productMode: SatelliteVuModeLike | SatelliteVuModeLike[],
): boolean {
  const mode = Array.isArray(productMode) ? productMode[0] : productMode;
  const allowedResolutions = mode?.resolution;
  const resolutionOptions = row?.resolution;

  if (!Array.isArray(allowedResolutions) || allowedResolutions.length === 0) {
    return false;
  }
  if (!Array.isArray(resolutionOptions) || resolutionOptions.length === 0) {
    return false;
  }

  row.selectedResolution = null;
  row.selectedResolutionValue = null;

  for (const option of resolutionOptions) {
    option.disable = !allowedResolutions.includes(option.value);
  }

  const preferredValue = allowedResolutions[0];
  const preferred = resolutionOptions.find(
    (r) => r.value === preferredValue && r.disable !== true,
  );
  const firstEnabled = preferred || resolutionOptions.find((r) => r.disable !== true);

  if (firstEnabled) {
    row.selectedResolution = firstEnabled;
    row.selectedResolutionValue = firstEnabled.value;
  }

  return true;
}

/**
 * SatelliteVu TIR: for L1B products we want a combined bands tooltip (VNIR + LWIR)
 * rather than only the currently selected resolution's bands.
 *
 * Returns a tooltip string when applicable, otherwise null.
 */
export function getSatelliteVuBandsTooltip(
  row: SensorRowWithResolution,
  productMode: SatelliteVuModeLike | SatelliteVuModeLike[],
): string | null {
  const mode = Array.isArray(productMode) ? productMode[0] : productMode;

  const productLevel =
    (mode as any)?.productLevel ?? (mode as any)?.productlevel ?? (mode as any)?.product_level;
  const isL1B =
    productLevel === 'L1B' || String((mode as any)?.key ?? '').toUpperCase().includes('L1B');

  if (!isL1B) return null;

  const resolutionOptions = row?.resolution;
  if (!Array.isArray(resolutionOptions) || resolutionOptions.length === 0) {
    return null;
  }

  const parts: string[] = [];
  const seen = new Set<string>();

  for (const option of resolutionOptions) {
    if (option?.disable === true) continue;
    const text = normalizeBandsText(option?.['bands']);
    if (!text) continue;
    if (seen.has(text)) continue;
    seen.add(text);
    parts.push(text);
  }

  return parts.length > 0 ? parts.join('\n') : null;
}
